import styled from "styled-components";
import { styled as styledMUI } from "@mui/material/styles";
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth0 } from "@auth0/auth0-react";
import { API_BASE } from '../../shared/Constants';


export interface AccountModalProps {
    openModal: boolean;
    setOpenModal: (open: boolean) => void;
}

function AccountModal({ openModal, setOpenModal }: AccountModalProps) {
    const { logout, getAccessTokenSilently } = useAuth0();

    const handleDeleteAccount = async () => {
        const confirmDelete = window.confirm("Você tem certeza que deseja apagar sua conta? Esta ação é irreversível.");
        if (confirmDelete) {
            const url = API_BASE + "/api/delete-account";
            const access_token = await getAccessTokenSilently()
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    "Authorization": `Auth ${access_token}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.status === 200) {
                logout({logoutParams: { returnTo: window.location.origin + "/auth" }});
            } else {
                alert("Erro ao apagar conta. Tente novamente mais tarde.");
            }
        }
    };

    return (
        <>
            <StyledModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                BackdropProps={{onClick: (event) => event.stopPropagation()}}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <ModalContainer>
                    <Header>
                        <Title>Excluir Minha Conta</Title>
                        <CloseButton onClick={() => setOpenModal(false)}>
                            <CloseIcon/>
                        </CloseButton>
                    </Header>
                    <Content>
                        <Desc>Ao apagar sua conta, todas as chaves de API, histórico de conversas no chat e créditos na plataforma serão <strong>permanentemente</strong> excluídos.</Desc>
                        <Desc>Para excluir sua conta, clique no botão abaixo.</Desc>
                        <DeleteAccountButton onClick={handleDeleteAccount}>Apagar Conta</DeleteAccountButton>
                    </Content>
                </ModalContainer>
            </StyledModal>
        </>
    );
}

export default AccountModal;


const StyledModal = styledMUI(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalContainer = styled.div`
    background: ${props => props.theme.modal_background};
    color: ${props => props.theme.font_color};
    border: 1px solid ${props => props.theme.modal_border};
    width: min(95vw, 600px);
    max-width: 100%;
    max-height: 90vh;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    outline: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    @media (max-width: 768px) {
        width: 98vw;
        max-height: 90vh;
        border-radius: 12px;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 30px 50px 15px 50px;
    border-bottom: 1px solid ${props => props.theme.modal_border};
    flex-shrink: 0;

    @media (max-width: 768px) {
        padding: 20px 16px 12px 16px;
    }
`;

const Title = styled.h1`
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
    color: ${props => props.theme.font_color};
    font-family: var(--font-primary);

    @media (max-width: 768px) {
        font-size: 1.4rem;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${props => props.theme.arrow_icon_color};
    padding: 8px;
    border-radius: 50%;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${props => props.theme.hover_overlay_light};
        color: ${props => props.theme.arrow_icon_color};
    }
`;

const Content = styled.div`
    padding: 30px 50px 40px 50px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (max-width: 768px) {
        padding: 20px 24px 32px 24px;
    }
`;

const Desc = styled.p`
    font-size: 1.15rem;
    color: ${props => props.theme.text_secondary || '#666666'};
    margin: 0;
    line-height: 1.6;
    font-family: var(--font-primary);

    strong {
        color: ${props => props.theme.font_color};
        font-weight: 600;
    }
`;

const DeleteAccountButton = styled.button`
    background-color: transparent;
    color: #991b1b;
    border: 1px solid #991b1b;
    border-radius: 8px;
    padding: 12px 24px;
    margin-top: 8px;
    font-size: 1.05rem;
    font-weight: 600;
    font-family: var(--font-primary);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: rgba(153, 27, 27, 0.05);
        border-color: #7f1d1d;
        color: #7f1d1d;
    }

    &:active {
        background-color: rgba(153, 27, 27, 0.1);
    }

    @media (max-width: 768px) {
        padding: 10px 20px;
        font-size: 1rem;
    }
`;
